import { Injectable } from "@angular/core";
import { BehaviorSubject, firstValueFrom } from "rxjs";
import { StorageService } from "src/app/core/services/storage.service";
import { WorkorderApiService } from "src/app/core/api/workorder-api.service";

import { IWorkflowStatus, IWorkflowStatusDto, SystemStatusSortOrder, WorkflowStatusField } from "../models/workflow-status.model";
/**
 * Service to manage workflow statuses
 * Handles loading and caching of dynamic workflow statuses from NEW API
 * Fetches from workflow_statusses table (GET only)
 */
@Injectable({
	providedIn: "root"
})
export class WorkflowStatusService {
	private statusesSubject$ = new BehaviorSubject<IWorkflowStatus[]>([]);
	private loadingSubject$ = new BehaviorSubject<boolean>(false);
	private loadingPromise: Promise<void> | null = null;

	/** Observable stream of workflow statuses */
	statuses$ = this.statusesSubject$.asObservable();

	/** Observable loading state */
	loading$ = this.loadingSubject$.asObservable();

	constructor(private storageService: StorageService, private workorderApiService: WorkorderApiService) {}

	/**
	 * Load workflow statuses from NEW API endpoint
	 * Filters for order_type = 1 (workshop), excludes archived statuses
	 * Deduplicates concurrent calls - multiple callers await the same fetch operation
	 */
	async loadWorkflowStatuses(): Promise<void> {
		// If there's already a request in progress, return that promise
		if (this.loadingPromise) {
			return this.loadingPromise;
		}

		// Create and cache the loading promise
		this.loadingPromise = this.performLoad();

		try {
			await this.loadingPromise;
		} finally {
			// Clear the cached promise when complete
			this.loadingPromise = null;
		}
	}

	/**
	 * Internal method to perform the actual loading operation
	 */
	private async performLoad(): Promise<void> {
		this.loadingSubject$.next(true);

		try {
			// Check cache first
			const cached = await this.storageService.get("workflow.statuses.v2");
			if (cached && cached.statuses && cached.timestamp && Date.now() - cached.timestamp < 60000) {
				// Use cached statuses if less than 1 minute old
				this.statusesSubject$.next(cached.statuses);
				this.loadingSubject$.next(false);
				return;
			}

			// Fetch from NEW API endpoint
			const statusDtos = await this.fetchWorkflowStatuses();

			// Filter and map to workflow statuses
			const workflowStatuses = this.mapDtosToWorkflowStatuses(statusDtos);

			// Cache the results
			await this.storageService.set("workflow.statuses.v2", {
				statuses: workflowStatuses,
				timestamp: Date.now()
			});

			this.statusesSubject$.next(workflowStatuses);
		} catch (error) {
			console.error("Error loading workflow statuses:", error);
		} finally {
			this.loadingSubject$.next(false);
		}
	}

	/**
	 * Fetch workflow statuses from NEW API endpoint
	 */
	private async fetchWorkflowStatuses(): Promise<IWorkflowStatusDto[]> {
		try {
			// Define fields to fetch
			const fields = [
				WorkflowStatusField.WorkflowStatusId,
				WorkflowStatusField.Description,
				WorkflowStatusField.Icon,
				WorkflowStatusField.SortOrder,
				WorkflowStatusField.OrderType,
				WorkflowStatusField.IsSystem,
				WorkflowStatusField.IsStockMutation,
				WorkflowStatusField.TimeRegistrationType,
				WorkflowStatusField.IsVehicleHere,
				WorkflowStatusField.IsBlocked,
				WorkflowStatusField.IsArchived,
				WorkflowStatusField.ToWorkflowStatusId,
				WorkflowStatusField.ToWorkflowStatusIdAfterClocking
			];

			// Filter for workshop (order_type=1) only - load all non-archived statuses
			// Component-level filtering will handle is_system
			const filter = "order_type[eq]:1,is_archived[eq]:0";
			const sort = "sort_order";

			const response = await firstValueFrom(this.workorderApiService.getWorkflowStatuses(1, fields, filter, sort));

			return response.data || [];
		} catch (error) {
			console.error("Error fetching workflow statuses from API:", error);
			throw error;
		}
	}

	/**
	 * Map workflow status DTOs from API to IWorkflowStatus
	 */
	private mapDtosToWorkflowStatuses(dtos: IWorkflowStatusDto[]): IWorkflowStatus[] {
		// Filter for workshop (order_type = 1) but KEEP archived statuses
		// Archived statuses are filtered out in selection UIs only
		const filtered = dtos.filter((dto) => dto.order_type === 1);

		// Map to IWorkflowStatus
		const mapped = filtered.map((dto) => ({
			workflowStatusId: dto.workflow_status_id,
			name: dto.description,
			icon: this.parseIconFromApi(dto.icon, dto.description, dto.sort_order),
			color: "primary", // Default color, can be enhanced later
			sortOrder: dto.sort_order,
			orderType: dto.order_type,
			isSystem: dto.is_system === 1,
			isStockMutation: dto.is_stock_mutation === 1,
			timeRegistrationType: (dto.time_registration_type ?? 0) as 0 | 1 | 9,
			isVehicleHere: dto.is_vehicle_here === 1,
			isArchived: dto.is_archived === 1,
			toWorkflowStatusId: dto.to_workflow_status_id || undefined,
			toWorkflowStatusIdAfterClocking: dto.to_workflow_status_id_after_clocking || undefined,
			count: 0
		}));

		// Sort by sort_order
		return mapped.sort((a, b) => a.sortOrder - b.sortOrder);
	}

	/**
	 * Parse icon from API format to array format
	 * API format: "fal fa-eye" → Array format: ["fal", "eye"]
	 * Falls back to client-side determination if icon not provided
	 */
	private parseIconFromApi(apiIcon: string | null | undefined, statusName: string, sortOrder: number): string[] {
		if (apiIcon && typeof apiIcon === "string") {
			// Parse "fal fa-eye" → ["fal", "eye"]
			// Parse "fal fa-360-degrees" → ["fal", "360-degrees"]
			const parts = apiIcon.trim().split(/\s+/);
			if (parts.length >= 2) {
				const style = parts[0]; // "fal", "fas", "far", etc.
				let iconName = parts[1].replace(/^fa-/, ""); // Remove "fa-" prefix if present

				return [style, iconName];
			}
		}
		// Fallback to client-side determination
		return this.getIconForStatus(statusName, sortOrder);
	}

	/**
	 * Get icon for a status based on its name and sort order (FALLBACK)
	 * Maps common Dutch status names and system sort orders to appropriate Font Awesome icons
	 *
	 * IMPORTANT: The entire Pro Light icon library is loaded in app.module.ts via addIconPacks(fal)
	 * This allows users to configure ANY Pro Light icon for custom workflow statuses.
	 *
	 * System status icons:
	 * - falCalendar (Afspraak/Appointment)
	 * - falEye (Inspectie/Inspection)
	 * - falHandshake (Offerte/Quote/Toestemming)
	 * - falCarMechanic (Onder handen/In Progress)
	 * - falFlagCheckered (Gereed/Ready/Completed)
	 * - falWrench (default for custom statuses)
	 *
	 * Users can configure custom icons from any Pro Light icon in the backend.
	 */
	private getIconForStatus(statusName: string, sortOrder: number): string[] {
		// Try matching by system sort order first (more reliable)
		switch (sortOrder) {
			case SystemStatusSortOrder.WORKSHOP_APPOINTMENT:
				return ["fal", "calendar"];
			case SystemStatusSortOrder.WORKSHOP_INSPECTION:
				return ["fal", "eye"];
			case SystemStatusSortOrder.WORKSHOP_QUOTE:
				return ["fal", "handshake"];
			case SystemStatusSortOrder.WORKSHOP_IN_PROGRESS:
				return ["fal", "car-mechanic"];
			case SystemStatusSortOrder.WORKSHOP_READY:
				return ["fal", "flag-checkered"];
		}

		// Default icon for custom statuses (API should always provide icons)
		return ["fal", "wrench"];
	}

	/**
	 * Get a status by workflow_status_id
	 */
	getStatusById(workflowStatusId: string): IWorkflowStatus | undefined {
		return this.statusesSubject$.value.find((status) => status.workflowStatusId === workflowStatusId);
	}

	/**
	 * Get all statuses (synchronous)
	 */
	getStatuses(): IWorkflowStatus[] {
		return this.statusesSubject$.value;
	}

	/**
	 * Clear cached statuses (force reload on next load)
	 */
	async clearCache(): Promise<void> {
		await this.storageService.remove("workflow.statuses.v2");
		this.loadingPromise = null;
	}

	/**
	 * Update status counts (for badge display)
	 * Ensures counts are always defined (never undefined) to prevent badge flickering
	 * Preserves visited status - already visited statuses keep their accurate counts
	 * @param counts Map of status IDs to their new counts
	 * @param forceUpdate If true, updates visited statuses too (used when moving workorders with accurate counts)
	 */
	updateStatusCounts(counts: Map<string, number>, forceUpdate: boolean = false): void {
		const statuses = this.statusesSubject$.value.map((status) => {
			// Preserve accurate counts for visited statuses (don't overwrite with estimated counts)
			// UNLESS forceUpdate is true (when we have accurate counts from moving workorders)
			if (status.isVisited && counts.has(status.workflowStatusId) && !forceUpdate) {
				return {
					...status,
					count: status.count ?? 0 // Keep current count
				};
			}
			// Update unvisited statuses (or all statuses if forceUpdate=true) with new counts
			return {
				...status,
				count: counts.has(status.workflowStatusId) ? counts.get(status.workflowStatusId)! : status.count ?? 0
			};
		});
		this.statusesSubject$.next(statuses);
	}

	/**
	 * Set all statuses (replaces current statuses)
	 * Used to update status properties like count and isVisited
	 */
	setStatuses(statuses: IWorkflowStatus[]): void {
		this.statusesSubject$.next(statuses);
	}
}
